import TextField from '@mui/material/TextField';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { CREATE_BRAND_RESET } from '../../constants/brandConstants';
import { createBrand, clearErrors } from '../../actions/brandAction';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import { useTheme, styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Button, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const NewBrand = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const theme = useTheme();

    const { loading, success, error } = useSelector((state) => state.newBrand);

    const [name, setName] = useState("");
    const [logo, setLogo] = useState("");
    const [logoPreview, setLogoPreview] = useState("");

    const handleLogoChange = (e) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (reader.readyState === 2) {
                setLogoPreview(reader.result);
                setLogo(reader.result);
            }
        };

        reader.readAsDataURL(e.target.files[0]);
    }

    const newBrandSubmitHandler = (e) => {
        e.preventDefault();

        if (!name.trim()) {
            enqueueSnackbar("Brand Name is required", { variant: "warning" });
            return;
        }

        const formData = new FormData();
        formData.set("name", name);
        if (logo) {
            formData.set("logo", logo);
        }

        dispatch(createBrand(formData));
    }

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (success) {
            enqueueSnackbar("Brand Created Successfully", { variant: "success" });
            dispatch({ type: CREATE_BRAND_RESET });
            navigate("/admin/products"); // Redirect to products page after creating brand
        }
    }, [dispatch, error, success, navigate, enqueueSnackbar]);

    return (
        <>
            <MetaData title="Admin: New Brand | Flipkart" />

            {loading && <BackdropLoader />}
            <motion.form
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                onSubmit={newBrandSubmitHandler}
                encType="multipart/form-data"
                className="flex flex-col sm:flex-row rounded-lg shadow p-4"
                id="mainform"
                style={{ backgroundColor: theme.palette.background.paper }}
            >

                <div className="flex flex-col gap-3 m-2 sm:w-1/2">
                    <TextField
                        label="Name"
                        variant="outlined"
                        size="small"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />

                    <div className="flex flex-col gap-2">
                        <h2 className="font-medium">Brand Logo</h2>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 100, height: 100, border: '2px dashed grey', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {logoPreview ? 
                                    <img draggable="false" src={logoPreview} alt="Brand Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : 
                                    <ImageIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                                }
                            </Box>
                            <Button
                                component="label"
                                variant="contained"
                                startIcon={<ImageIcon />}
                            >
                                Upload Logo
                                <VisuallyHiddenInput type="file" accept="image/*" onChange={handleLogoChange} />
                            </Button>
                        </Box>
                    </div>

                    <div className="flex justify-end">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                type="submit"
                                form="mainform"
                                size="large"
                                sx={{ bgcolor: theme.palette.primary.main, width: '10rem' }}
                            >
                                Create
                            </Button>
                        </motion.div>
                    </div>

                </div>

            </motion.form>
        </>
    );
};

export default NewBrand;